/**
 * Le paramétrage du transport : les deux tables que Chris a préparées sous Excel.
 *
 * *Demande du 01/09/2026 : « j'aimerai créer un nouveau menu paramétrage transport dans l'appli
 * avec ces deux tables, tu dois me laisser la possibilité de rajouter des couples ou des
 * combinaisons, je dois aussi pouvoir rajouter des lignes de matériels ou en supprimer ou en
 * modifier dans chacune des deux tables. […] les deux tables doivent être indépendantes. »*
 *
 * ## Deux tables, une même forme, et une différence de fond
 *
 * Les deux sont des **tableaux croisés** : les matériels en lignes, les groupes en colonnes, une
 * case à cocher à l'intersection. C'est la forme du classeur que Chris tient déjà, et c'est
 * aussi celle de la grille tarifaire — on ne réinvente pas un geste que l'écran voisin a déjà.
 *
 * Ce qui les sépare tient en une colonne :
 *
 *  - un **couple** est un attelage qui emporte de la matière (`TRACTEUR 4X2 + BENNE ALU`), donc
 *    il a un **tonnage** — porté par la ligne, hérité par le couple ;
 *  - une **combinaison** dit ce qui monte ensemble sur un voyage (`PELLE 0-5t + CHARGEUR 0-5t`).
 *    Aucun porte-engin n'y figure : elle décrit ce qui est porté, pas ce qui porte. Un tonnage
 *    n'y voudrait rien dire.
 *
 * ## Ce que l'écran ne laisse PAS faire, et pourquoi
 *
 * Les colonnes descriptives — code, libellé, famille, coût, acheminement — sont **en lecture**.
 * Elles viennent du référentiel matériel et s'y corrigent, à un seul endroit *(tranché avec
 * Chris le 01/09)*. Les rendre modifiables ici donnerait deux réponses à la même question, ce
 * que le produit a déjà fermé pour l'acheminement le 20/08. Ce qui s'édite ici est ce qui
 * n'existe nulle part ailleurs : le tonnage, et les croix.
 *
 * ## Ce que l'écran compte et affiche
 *
 * Un tonnage manquant se voit et se compte. C'est la règle du 18/08 sur le zéro qui ne se
 * confond avec rien, portée au transport : un attelage sans tonnage n'est pas un attelage à
 * vide, c'est une réponse qui manque — et une liste de chargement incomplète se lit exactement
 * comme une liste complète. Mesuré sur le classeur de Chris : trois couples remplis sur onze
 * n'avaient aucun tonnage.
 */
import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import {
  adresseClasseurIndemnites,
  adresseClasseurPaniers,
  adresseClasseurTransport,
  ajouterLigneTransport,
  ajouterZoneIndemnite,
  ajouterZonePanier,
  chargerBaremeIndemnites,
  chargerBaremePaniers,
  chargerGeneriques,
  chargerTransport,
  cocherTransport,
  creerGroupeTransport,
  creerPeriodeIndemnite,
  creerPeriodePanier,
  dupliquerPeriodeIndemnite,
  dupliquerPeriodePanier,
  fixerTonnageTransport,
  importerBaremeIndemnites,
  importerBaremePaniers,
  importerTransport,
  modifierPeriodeIndemnite,
  modifierPeriodePanier,
  modifierZoneIndemnite,
  modifierZonePanier,
  retirerLigneTransport,
  supprimerGroupeTransport,
  supprimerPeriodeIndemnite,
  supprimerPeriodePanier,
  supprimerZoneIndemnite,
  supprimerZonePanier,
} from "./api";
import type {
  BaremeIndemnites,
  BaremePaniers,
  BilanIndemnites,
  BilanPaniers,
  BilanTransport,
  ContenuTransport,
  TableTransport,
} from "./api";
import type { Generique } from "@planif/moteur";
import { ReglagesTransport } from "./ReglagesTransport";

/** Ce qui change d'une table à l'autre, rassemblé là où on peut le lire d'un coup. */
const TABLES = {
  couples: {
    titre: "Transport de matériaux — les couples d'attelage",
    aide:
      "Un couple est un attelage qui emporte de la matière : un tracteur et sa benne, ou une " +
      "machine qui porte seule. Le tonnage est la charge utile, en tonnes ; il se saisit sur " +
      "la ligne et vaut pour tous les couples qui la citent.",
    mot: "couple",
    mots: "couples",
    avecTonnage: true,
  },
  combinaisons: {
    titre: "Transfert de matériel — les combinaisons de chargement",
    aide:
      "Une combinaison est ce qui monte ensemble sur un même voyage. Le porte-engin n'y figure " +
      "pas : elle dit ce qui est porté, pas ce qui porte. Une machine absente de toute " +
      "combinaison compte pour un voyage à elle seule.",
    mot: "combinaison",
    mots: "combinaisons",
    avecTonnage: false,
  },
} as const;

type Etat =
  | { phase: "chargement" }
  | { phase: "chargee"; contenu: ContenuTransport }
  | { phase: "erreur"; raison: string };

/** Un champ qui n'envoie sa valeur qu'une fois la saisie terminée. */
function Champ({
  valeur,
  placeholder,
  enAttente,
  ariaLabel,
  aRemplir,
  onValider,
}: {
  valeur: string;
  placeholder?: string;
  enAttente: boolean;
  ariaLabel: string;
  /**
   * Faut-il signaler ce champ comme une réponse qui manque ? Par défaut, oui dès qu'il est vide.
   *
   * L'exception vient des deux formes d'indemnité (053) : une zone vaut un montant OU une part
   * du salaire brut, donc **l'une des deux cases est forcément vide et c'est la bonne réponse**.
   * Le surlignage « à remplir » criait sur six cases par barème — mesuré à l'écran —, ce qui est
   * exactement l'inverse de ce qu'il sert à dire : il ne doit signaler que ce qui manque
   * vraiment, sinon on cesse de le regarder.
   */
  aRemplir?: boolean;
  onValider: (valeur: string) => void;
}): JSX.Element {
  const [brouillon, setBrouillon] = useState<string | null>(null);
  const signaler = aRemplir ?? valeur === "";

  return (
    <input
      className={`saisie${enAttente ? " saisie--attente" : ""}${signaler ? " saisie--a-remplir" : ""}`}
      value={brouillon ?? valeur}
      placeholder={placeholder}
      disabled={enAttente}
      aria-label={ariaLabel}
      onChange={(e) => {
        setBrouillon(e.target.value);
      }}
      onFocus={(e) => {
        e.target.select();
      }}
      onBlur={() => {
        const saisi = brouillon;
        setBrouillon(null);
        if (saisi !== null && saisi.trim() !== valeur) onValider(saisi.trim());
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setBrouillon(null);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/** Le compte rendu d'un réimport. */
function Bilan({ bilan }: { bilan: BilanTransport }): JSX.Element {
  return (
    <div className="import__bilan">
      <p>
        <strong>{bilan.lignesAjoutees}</strong> ligne(s) ajoutée(s),{" "}
        <strong>{bilan.lignesConnues}</strong> déjà connue(s)
        {bilan.tonnagesPoses > 0 && (
          <>
            , <strong>{bilan.tonnagesPoses}</strong> tonnage(s) posé(s)
          </>
        )}
        .
      </p>
      <p>
        Les colonnes ont été <strong>remplacées</strong> : {bilan.groupesAvant} avant,{" "}
        <strong>{bilan.groupesApres}</strong> après.
        {bilan.colonnesVides > 0 && (
          <>
            {" "}
            {bilan.colonnesVides} colonne(s) du classeur étaient vides — elles ne créent rien,
            la réserve de paramétrage se fait ici avec le bouton d'ajout.
          </>
        )}
      </p>
      {bilan.refusees.length > 0 && (
        <>
          <p className="alerte">
            {bilan.refusees.length} ligne(s) refusée(s) — rien n'a été inventé à leur place :
          </p>
          <ul className="import__refusees">
            {bilan.refusees.map((r) => (
              <li key={`${String(r.ligne)}-${r.raison}`}>
                ligne {r.ligne} du classeur : {r.raison}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Les indemnités par zone (053)
 * ------------------------------------------------------------------------- */

/**
 * Où en est une période, par rapport au jour que la BASE a rendu.
 *
 * Jamais `new Date()` : la règle de `Periode.tsx`. Un poste réglé sur la veille annoncerait le
 * barème de l'année close le 1er janvier, et rien ne le dirait.
 */
function etatDeLaPeriode(
  periode: { debut: string; fin: string },
  aujourdhui: string,
): { mot: string; classe: string } {
  if (aujourdhui === "") return { mot: "", classe: "" };
  if (aujourdhui < periode.debut) return { mot: "à venir", classe: "zones__etat--avenir" };
  if (aujourdhui > periode.fin) return { mot: "close", classe: "zones__etat--close" };
  return { mot: "en vigueur", classe: "zones__etat--vigueur" };
}

/**
 * Une borne de kilomètres, telle qu'on l'écrit.
 *
 * La base rend `numeric(8,2)`, donc « 10.00 » là où le classeur écrit « 10 ». Les deux
 * décimales servent aux distances au dixième (« 10.5 »), pas à afficher deux zéros sur toutes
 * les lignes : le tableau doit se relire face au classeur, et « 10.00 » face à « 10 » fait
 * douter à chaque ligne.
 *
 * L'allègement est **d'affichage seul** — la valeur enregistrée ne change pas, et la
 * comparaison se fait en nombres à l'écriture, donc réafficher « 10 » ne provoque aucune
 * écriture au premier passage dans le champ.
 */
function borneLisible(valeur: string): string {
  if (!/^-?\d+\.\d+$/u.test(valeur)) return valeur;
  return valeur.replace(/\.?0+$/u, "");
}

/** Une borne de période. Le calendrier du système, pas un calendrier dessiné à la main. */
function BorneDate({
  valeur,
  etiquette,
  enAttente,
  onValider,
}: {
  valeur: string;
  etiquette: string;
  enAttente: boolean;
  onValider: (valeur: string) => void;
}): JSX.Element {
  const [brouillon, setBrouillon] = useState<string | null>(null);
  return (
    <label>
      {etiquette}{" "}
      <input
        type="date"
        value={brouillon ?? valeur}
        disabled={enAttente}
        onChange={(e) => {
          setBrouillon(e.target.value);
        }}
        onBlur={() => {
          const saisie = brouillon;
          setBrouillon(null);
          if (saisie !== null && saisie !== "" && saisie !== valeur) onValider(saisie);
        }}
      />
    </label>
  );
}

/**
 * Le panneau des périodes de validité : celle qu'on regarde, ses bornes, et la suivante.
 *
 * *« Cette table doit avoir une date de validité du 01/01/2026 au 31/12/2026 ; tous les ans nous
 * modifierons les montants. Il faudra historiser ces montants. »* — c'est ce panneau qui rend la
 * demande tenable, et le bouton qui compte est **« en reprenant les zones de »** : sans lui, le
 * barème de l'an prochain se retaperait à la main, et un chiffre retapé de travers ne se voit pas.
 *
 * Les bornes proposées viennent du **serveur** et enchaînent sur la dernière période : la
 * contrainte de non-chevauchement refuserait autre chose, et laisser l'administrateur retrouver
 * lui-même la bonne date serait lui faire deviner une règle de la base.
 */
function PeriodesIndemnite({
  bareme,
  enAttente,
  onChoisir,
  onAgir,
}: {
  bareme: BaremeIndemnites;
  enAttente: boolean;
  onChoisir: (id: string) => void;
  onAgir: (action: Promise<unknown>) => void;
}): JSX.Element {
  const [ouvert, setOuvert] = useState(false);
  const [libelle, setLibelle] = useState(bareme.periodeProposee.libelle);
  const [debut, setDebut] = useState(bareme.periodeProposee.debut);
  const [fin, setFin] = useState(bareme.periodeProposee.fin);
  const [depuis, setDepuis] = useState<string>(bareme.periode?.id ?? "");

  const courante = bareme.periode;
  const complet = libelle.trim() !== "" && debut !== "" && fin !== "";

  return (
    <div className="periodes">
      <p className="etat">
        <label>
          Période de validité{" "}
          <select
            value={courante?.id ?? ""}
            disabled={bareme.periodes.length === 0}
            aria-label="Période de validité des indemnités"
            onChange={(e) => {
              onChoisir(e.target.value);
            }}
          >
            {bareme.periodes.length === 0 && <option value="">aucune période</option>}
            {bareme.periodes.map((p) => (
              <option key={p.id} value={p.id}>
                {p.libelle} ({p.debut} → {p.fin})
              </option>
            ))}
          </select>
        </label>
        {courante !== null && (
          <>
            {" "}
            <span className={`zones__etat ${etatDeLaPeriode(courante, bareme.aujourdhui).classe}`}>
              {etatDeLaPeriode(courante, bareme.aujourdhui).mot}
            </span>
          </>
        )}
        {" · "}
        <button
          type="button"
          className="lien"
          onClick={() => {
            setOuvert(!ouvert);
          }}
        >
          {ouvert ? "fermer" : "gérer les périodes"}
        </button>
      </p>

      {ouvert && (
        <div className="periodes__panneau">
          {courante !== null && (
            <form
              className="ajout"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <span className="periodes__titre">Corriger « {courante.libelle} »</span>
              <Champ
                valeur={courante.libelle}
                enAttente={enAttente}
                ariaLabel="Libellé de la période"
                onValider={(valeur) => {
                  onAgir(modifierPeriodeIndemnite(courante.id, { libelle: valeur }));
                }}
              />
              <BorneDate
                valeur={courante.debut}
                etiquette="Début"
                enAttente={enAttente}
                onValider={(valeur) => {
                  onAgir(modifierPeriodeIndemnite(courante.id, { debut: valeur }));
                }}
              />
              <BorneDate
                valeur={courante.fin}
                etiquette="Fin"
                enAttente={enAttente}
                onValider={(valeur) => {
                  onAgir(modifierPeriodeIndemnite(courante.id, { fin: valeur }));
                }}
              />
              {/*
                La suppression ne demande pas confirmation : le serveur REFUSE d'effacer une
                période qui porte des zones — c'est l'historique. Le seul cas qui passe est
                celle qu'on vient de créer de travers.
              */}
              <button
                type="button"
                className="lien"
                disabled={enAttente}
                onClick={() => {
                  onAgir(supprimerPeriodeIndemnite(courante.id));
                }}
              >
                Supprimer
              </button>
            </form>
          )}

          <form
            className="ajout"
            onSubmit={(e) => {
              e.preventDefault();
              if (!complet) return;
              const demande = { libelle: libelle.trim(), debut, fin };
              onAgir(
                depuis === ""
                  ? creerPeriodeIndemnite(demande)
                  : dupliquerPeriodeIndemnite(depuis, demande),
              );
            }}
          >
            <span className="periodes__titre">Nouvelle période</span>
            <input
              value={libelle}
              placeholder="Libellé"
              aria-label="Libellé de la nouvelle période"
              onChange={(e) => {
                setLibelle(e.target.value);
              }}
            />
            <label>
              du{" "}
              <input
                type="date"
                value={debut}
                aria-label="Début de la nouvelle période"
                onChange={(e) => {
                  setDebut(e.target.value);
                }}
              />
            </label>
            <label>
              au{" "}
              <input
                type="date"
                value={fin}
                aria-label="Fin de la nouvelle période"
                onChange={(e) => {
                  setFin(e.target.value);
                }}
              />
            </label>
            {bareme.periodes.length > 0 && (
              <label>
                en reprenant les zones de{" "}
                <select
                  value={depuis}
                  aria-label="Période dont reprendre les zones"
                  onChange={(e) => {
                    setDepuis(e.target.value);
                  }}
                >
                  <option value="">aucune (barème vide)</option>
                  {bareme.periodes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.libelle}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button type="submit" disabled={!complet || enAttente}>
              Créer
            </button>
          </form>

          <p className="note">
            Deux périodes ne peuvent pas se chevaucher : l'indemnité d'un jour donné doit être
            décidable. Reprendre les zones d'une période donne un point de départ à corriger, pas
            une reconduction — c'est à vous de changer les montants qui ont bougé. La période
            passée reste : c'est elle qui porte l'historique.
          </p>
        </div>
      )}
    </div>
  );
}

/** Le compte rendu d'un réimport de barème. */
function BilanZones({ bilan }: { bilan: BilanIndemnites }): JSX.Element {
  return (
    <div className="import__bilan">
      <p>
        Les zones ont été <strong>remplacées</strong> : {bilan.zonesAvant} avant,{" "}
        <strong>{bilan.zonesApres}</strong> après — dont {bilan.montantsPoses} montant(s) en euros
        et {bilan.partsPosees} part(s) du salaire brut.
        {bilan.sansIndemnite > 0 && (
          <>
            {" "}
            <strong>{bilan.sansIndemnite} zone(s) sans indemnité</strong> : la case était vide, et
            rien n'a été inventé à sa place.
          </>
        )}
      </p>
      {/*
        Ce qui a été INTERPRÉTÉ, dit une par une. « 50% salaire brut » est du texte libre : le
        lire comme un pourcentage est une lecture, et une lecture silencieuse est le pire de ce
        qu'un import peut faire.
      */}
      {bilan.interpretations.length > 0 && (
        <>
          <p>Ce qui n'était pas un nombre, et ce qui en a été compris :</p>
          <ul className="import__refusees">
            {bilan.interpretations.map((i) => (
              <li key={`${i.zone}-${i.lu}`}>
                {i.zone} : « {i.lu} » lu comme <strong>{i.compris}</strong>
              </li>
            ))}
          </ul>
        </>
      )}
      {bilan.refusees.length > 0 && (
        <>
          <p className="alerte">
            {bilan.refusees.length} ligne(s) refusée(s) — rien n'a été inventé à leur place :
          </p>
          <ul className="import__refusees">
            {bilan.refusees.map((r) => (
              <li key={`${String(r.ligne)}-${r.raison}`}>
                ligne {r.ligne} du classeur : {r.raison}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

type EtatBareme =
  | { phase: "chargement" }
  | { phase: "charge"; bareme: BaremeIndemnites }
  | { phase: "erreur"; raison: string };

/**
 * Le tableau des indemnités par zone de transport.
 *
 * *Demande de Chris, 02/09/2026, classeur `Zones_transport.xlsx` à l'appui, et placé ici sur sa
 * consigne : « dans le menu Paramétrage transport, avant le tableau transport de matériaux ».*
 *
 * ## Deux formes d'indemnité, et deux colonnes
 *
 * Le classeur porte cinq montants en euros et, sur la dernière zone, « 50% salaire brut ». Ce
 * n'est pas un montant mais une règle de calcul : la ranger dans la colonne des euros en aurait
 * fait 50 €. Les deux colonnes sont donc distinctes et **exclusives** — remplir l'une vide
 * l'autre, et c'est le serveur qui le tient, pas cet écran.
 *
 * ## Le chevron de la borne basse est réaffiché, pas stocké
 *
 * Le classeur écrit `>10` ; la base garde `10`. Le « > » dit seulement que la borne basse est
 * exclue — vrai de toutes les tranches sauf la première, qui part de 0 —, et l'écran le remet
 * devant le nombre. Le stocker aurait interdit de comparer une distance à une borne.
 *
 * ## Ce que l'écran compte, et pourquoi il le compte
 *
 * Un barème peut être enregistré et faux sans que rien ne plante : deux tranches qui se
 * recouvrent rendent l'indemnité d'une distance indécidable, un trou entre deux tranches laisse
 * une distance sans zone, une case vide n'est pas zéro euro. Aucun des trois n'empêche la saisie
 * — on paramètre par étapes —, mais **tous les trois s'affichent**. C'est la règle de la donnée
 * manquante : un tableau qui se tait se lit « tout va bien ».
 */
function ZonesIndemnite(): JSX.Element {
  const [etat, setEtat] = useState<EtatBareme>({ phase: "chargement" });
  const [voulue, setVoulue] = useState<string | null>(null);
  const [enAttente, setEnAttente] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [bilan, setBilan] = useState<BilanIndemnites | null>(null);
  const [nouvelle, setNouvelle] = useState({ libelle: "", kmMin: "", kmMax: "", montant: "", part: "" });
  const champFichier = useRef<HTMLInputElement>(null);

  const recharger = (id: string | null = voulue): void => {
    chargerBaremeIndemnites(id)
      .then((bareme) => {
        setEtat({ phase: "charge", bareme });
      })
      .catch((e: unknown) => {
        setEtat({ phase: "erreur", raison: e instanceof Error ? e.message : String(e) });
      });
  };

  useEffect(() => {
    recharger(voulue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voulue]);

  /** Tout geste passe par ici : une seule façon d'attendre, d'échouer et de recharger. */
  const agir = (action: Promise<unknown>): void => {
    setEnAttente(true);
    setErreur(null);
    action
      .then(() => {
        recharger();
      })
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setEnAttente(false);
      });
  };

  if (etat.phase === "chargement") return <p>Chargement des indemnités…</p>;
  if (etat.phase === "erreur") return <p className="alerte">{etat.raison}</p>;

  const { bareme } = etat;
  const periode = bareme.periode;
  const { zones, coherence } = bareme;
  const complet = nouvelle.libelle.trim() !== "" && nouvelle.kmMin !== "" && nouvelle.kmMax !== "";

  return (
    <section className="referentiel">
      <h2>
        Indemnités par zone de transport — {zones.length} zone(s)
        {periode !== null && <> · {periode.libelle}</>}
      </h2>
      <p className="etat">
        Une zone est une <strong>tranche de kilomètres</strong> et ce qu'elle vaut : un montant en
        euros, ou une part du salaire brut. Les bornes se lisent « au-delà de la basse, jusqu'à la
        haute incluse » — c'est le « &gt; » du classeur, réaffiché ici et non stocké. Le barème
        appartient à une <strong>période de validité</strong> : celui de l'an prochain se crée à
        côté, celui de cette année reste.
      </p>

      <PeriodesIndemnite
        bareme={bareme}
        enAttente={enAttente}
        onChoisir={setVoulue}
        onAgir={agir}
      />

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      {periode === null ? (
        <p className="alerte">
          Aucune période de validité : le barème n'a pas encore d'année. En créer une ci-dessus —
          la proposition porte déjà les bonnes bornes.
        </p>
      ) : (
        <>
          {/* Ce qui manque ou ce qui se contredit se COMPTE et s'affiche. */}
          <p className="etat">
            {coherence.sansIndemnite.length === 0 ? (
              <>Toutes les zones portent une indemnité.</>
            ) : (
              <strong>
                {coherence.sansIndemnite.length} zone(s) sans indemnité —{" "}
                {coherence.sansIndemnite.join(", ")}.
              </strong>
            )}
            {" · "}
            {coherence.chevauchements.length === 0 && coherence.trous.length === 0 ? (
              <>
                Les tranches s'enchaînent sans trou
                {coherence.couverteJusqua !== null && (
                  <>, jusqu'à {borneLisible(coherence.couverteJusqua)} km</>
                )}
                .
              </>
            ) : (
              <strong>
                {coherence.chevauchements.length > 0 && (
                  <>
                    {coherence.chevauchements
                      .map((c) => `${c.premiere} recouvre ${c.seconde}`)
                      .join(" ; ")}
                    .{" "}
                  </>
                )}
                {coherence.trous.length > 0 && (
                  <>
                    Aucune zone entre{" "}
                    {coherence.trous.map((t) => `${t.apres} et ${t.avant}`).join(" ; ")}.
                  </>
                )}
              </strong>
            )}
          </p>

          {/*
            L'aller-retour par Excel. La différence avec les deux tables du dessous est dite
            AVANT le clic, parce qu'elle est contraire : ici les zones sont REMPLACÉES, une zone
            absente du classeur disparaît. Une tranche est un morceau d'un découpage continu —
            en garder une que le classeur ne porte plus superposerait deux découpages.
          */}
          <p className="etat">
            <a href={adresseClasseurIndemnites(periode.id)} download>
              Exporter le classeur
            </a>
            {" · "}
            <button
              type="button"
              className="lien"
              onClick={() => champFichier.current?.click()}
            >
              Réimporter un classeur dans « {periode.libelle} »
            </button>
            <input
              ref={champFichier}
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={(e) => {
                const fichier = e.target.files?.[0];
                e.target.value = "";
                if (fichier === undefined) return;
                setBilan(null);
                setErreur(null);
                importerBaremeIndemnites(periode.id, fichier)
                  .then((resultat) => {
                    setBilan(resultat);
                    recharger();
                  })
                  .catch((err: unknown) => {
                    setErreur(err instanceof Error ? err.message : String(err));
                  });
              }}
            />
            {" · "}
            <span className="note">
              Les zones de cette période sont <strong>remplacées</strong> par celles du classeur —
              une zone absente disparaît. L'export sort les deux formes d'indemnité en deux
              colonnes de nombres ; le classeur d'origine, qui les mêle dans une seule colonne
              « Indemnité », se relit aussi.
            </span>
          </p>

          {bilan !== null && <BilanZones bilan={bilan} />}

          <div className="transport__defilement">
            <table className="ressources transport__table zones__table">
              {/*
                La grille des colonnes. Sans elle, `table-layout: fixed` sur `width: 100%`
                partage la fenêtre à parts égales — la panne corrigée le 01/09/2026 sur les
                quatre autres référentiels.
              */}
              <colgroup>
                <col style={{ width: "calc(16ch + 0.7rem)" }} />
                <col style={{ width: "calc(12ch + 0.7rem)" }} />
                <col style={{ width: "calc(12ch + 0.7rem)" }} />
                <col style={{ width: "calc(14ch + 0.7rem)" }} />
                <col style={{ width: "calc(16ch + 0.7rem)" }} />
                <col style={{ width: "5.5rem" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Zone</th>
                  <th className="col-nombre">Km au-delà de</th>
                  <th className="col-nombre">Km jusqu'à</th>
                  <th className="col-nombre">Indemnité (€)</th>
                  <th className="col-nombre">% du salaire brut</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {zones.map((z) => (
                  <tr key={z.id}>
                    <td>
                      <Champ
                        valeur={z.libelle}
                        enAttente={enAttente}
                        ariaLabel={`Nom de la zone ${z.libelle}`}
                        onValider={(valeur) => {
                          if (valeur !== "") agir(modifierZoneIndemnite(z.id, { libelle: valeur }));
                        }}
                      />
                    </td>
                    <td className="col-nombre">
                      {/*
                        Le chevron est réaffiché, jamais stocké : la borne basse est exclue, sauf
                        pour la tranche qui part de 0.

                        Il vit dans un conteneur en `flex` avec le champ, et non simplement posé
                        devant : `table.ressources .saisie` impose `width: 100%` au champ, donc
                        le chevron s'ajoutait aux cent pour cent et poussait le champ de seize
                        pixels hors de sa cellule — mesuré au navigateur à 900, 1440 et 1920 px.
                        En `flex`, le champ prend ce qui reste.
                      */}
                      <span className="zones__borne">
                        <span className="zones__chevron">{Number(z.kmMin) > 0 ? ">" : ""}</span>
                        <Champ
                          valeur={borneLisible(z.kmMin)}
                          enAttente={enAttente}
                          ariaLabel={`Borne basse de ${z.libelle}`}
                          onValider={(valeur) => {
                            if (valeur !== "") agir(modifierZoneIndemnite(z.id, { kmMin: valeur }));
                          }}
                        />
                      </span>
                    </td>
                    <td className="col-nombre">
                      <Champ
                        valeur={borneLisible(z.kmMax)}
                        enAttente={enAttente}
                        ariaLabel={`Borne haute de ${z.libelle}`}
                        onValider={(valeur) => {
                          if (valeur !== "") agir(modifierZoneIndemnite(z.id, { kmMax: valeur }));
                        }}
                      />
                    </td>
                    {/*
                      Les deux cases d'indemnité ne se signalent « à remplir » que si les DEUX
                      sont vides. Une zone vaut un montant ou une part, donc l'une des deux est
                      forcément vide sur une zone complète : surligner celle-là ferait crier
                      l'écran sur la bonne réponse, et six fois par barème.
                    */}
                    <td className="col-nombre">
                      <Champ
                        valeur={z.montant ?? ""}
                        placeholder="—"
                        enAttente={enAttente}
                        aRemplir={z.montant === null && z.partSalaireBrut === null}
                        ariaLabel={`Indemnité en euros de ${z.libelle}`}
                        onValider={(valeur) => {
                          agir(modifierZoneIndemnite(z.id, { montant: valeur === "" ? null : valeur }));
                        }}
                      />
                    </td>
                    <td className="col-nombre">
                      <Champ
                        valeur={z.partSalaireBrut ?? ""}
                        placeholder="—"
                        enAttente={enAttente}
                        aRemplir={z.montant === null && z.partSalaireBrut === null}
                        ariaLabel={`Part du salaire brut de ${z.libelle}`}
                        onValider={(valeur) => {
                          agir(
                            modifierZoneIndemnite(z.id, {
                              partSalaireBrut: valeur === "" ? null : valeur,
                            }),
                          );
                        }}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="lien"
                        disabled={enAttente}
                        title="Retirer cette zone du barème de cette période. Les autres périodes n'y touchent pas."
                        onClick={() => {
                          agir(supprimerZoneIndemnite(z.id));
                        }}
                      >
                        Retirer
                      </button>
                    </td>
                  </tr>
                ))}
                {zones.length === 0 && (
                  <tr>
                    <td colSpan={6} className="vide">
                      Aucune zone dans cette période. En ajouter une ci-dessous, réimporter le
                      classeur, ou créer la période en reprenant les zones d'une autre.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <form
            className="ajout"
            onSubmit={(e) => {
              e.preventDefault();
              if (!complet) return;
              const demande = {
                libelle: nouvelle.libelle.trim(),
                kmMin: nouvelle.kmMin,
                kmMax: nouvelle.kmMax,
                montant: nouvelle.montant === "" ? null : nouvelle.montant,
                partSalaireBrut: nouvelle.part === "" ? null : nouvelle.part,
              };
              setNouvelle({ libelle: "", kmMin: "", kmMax: "", montant: "", part: "" });
              agir(ajouterZoneIndemnite(periode.id, demande));
            }}
          >
            <span className="periodes__titre">Nouvelle zone</span>
            <input
              value={nouvelle.libelle}
              placeholder="Zone7"
              aria-label="Nom de la nouvelle zone"
              onChange={(e) => {
                setNouvelle({ ...nouvelle, libelle: e.target.value });
              }}
            />
            <label>
              au-delà de{" "}
              <input
                className="ajout__nombre"
                value={nouvelle.kmMin}
                placeholder="km"
                aria-label="Borne basse de la nouvelle zone"
                onChange={(e) => {
                  setNouvelle({ ...nouvelle, kmMin: e.target.value });
                }}
              />
            </label>
            <label>
              jusqu'à{" "}
              <input
                className="ajout__nombre"
                value={nouvelle.kmMax}
                placeholder="km"
                aria-label="Borne haute de la nouvelle zone"
                onChange={(e) => {
                  setNouvelle({ ...nouvelle, kmMax: e.target.value });
                }}
              />
            </label>
            <label>
              indemnité{" "}
              <input
                className="ajout__nombre"
                value={nouvelle.montant}
                placeholder="€"
                aria-label="Indemnité en euros de la nouvelle zone"
                onChange={(e) => {
                  setNouvelle({ ...nouvelle, montant: e.target.value, part: "" });
                }}
              />
            </label>
            <label>
              ou{" "}
              <input
                className="ajout__nombre"
                value={nouvelle.part}
                placeholder="% brut"
                aria-label="Part du salaire brut de la nouvelle zone"
                onChange={(e) => {
                  setNouvelle({ ...nouvelle, part: e.target.value, montant: "" });
                }}
              />
            </label>
            <button type="submit" disabled={!complet || enAttente}>
              Ajouter la zone
            </button>
          </form>
          <p className="note">
            Un montant <strong>ou</strong> une part du salaire brut, jamais les deux : remplir
            l'un vide l'autre. Laisser les deux vides est permis — c'est une indemnité qui reste à
            trancher, et l'écran la compte.
          </p>
        </>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * Les paniers par zone (054)
 * ------------------------------------------------------------------------- */

/** Le compte rendu d'un réimport de barème de paniers. */
function BilanPaniersLu({ bilan }: { bilan: BilanPaniers }): JSX.Element {
  return (
    <div className="import__bilan">
      <p>
        Les zones ont été <strong>remplacées</strong> : {bilan.zonesAvant} avant,{" "}
        <strong>{bilan.zonesApres}</strong> après — dont {bilan.montantsPoses} montant(s).
        {bilan.sansMontant > 0 && (
          <>
            {" "}
            <strong>{bilan.sansMontant} zone(s) sans montant</strong> : la case était vide, et rien
            n'a été inventé à sa place.
          </>
        )}
      </p>
      {bilan.refusees.length > 0 && (
        <>
          <p className="alerte">
            {bilan.refusees.length} ligne(s) refusée(s) — rien n'a été inventé à leur place :
          </p>
          <ul className="import__refusees">
            {bilan.refusees.map((r) => (
              <li key={`${String(r.ligne)}-${r.raison}`}>
                ligne {r.ligne} du classeur : {r.raison}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * Le panneau des périodes de validité des paniers.
 *
 * Jumeau de celui des indemnités, et **volontairement distinct** : rien ne garantit que les
 * paniers et les indemnités changeront aux mêmes dates, et une période commune obligerait à
 * rouvrir le barème des indemnités pour corriger un panier.
 */
function PeriodesPanier({
  bareme,
  enAttente,
  onChoisir,
  onAgir,
}: {
  bareme: BaremePaniers;
  enAttente: boolean;
  onChoisir: (id: string) => void;
  onAgir: (action: Promise<unknown>) => void;
}): JSX.Element {
  const [ouvert, setOuvert] = useState(false);
  const [libelle, setLibelle] = useState(bareme.periodeProposee.libelle);
  const [debut, setDebut] = useState(bareme.periodeProposee.debut);
  const [fin, setFin] = useState(bareme.periodeProposee.fin);
  const [depuis, setDepuis] = useState<string>(bareme.periode?.id ?? "");

  const courante = bareme.periode;
  const complet = libelle.trim() !== "" && debut !== "" && fin !== "";

  return (
    <div className="periodes">
      <p className="etat">
        <label>
          Période de validité{" "}
          <select
            value={courante?.id ?? ""}
            disabled={bareme.periodes.length === 0}
            aria-label="Période de validité des paniers"
            onChange={(e) => {
              onChoisir(e.target.value);
            }}
          >
            {bareme.periodes.length === 0 && <option value="">aucune période</option>}
            {bareme.periodes.map((p) => (
              <option key={p.id} value={p.id}>
                {p.libelle} ({p.debut} → {p.fin})
              </option>
            ))}
          </select>
        </label>
        {courante !== null && (
          <>
            {" "}
            <span className={`zones__etat ${etatDeLaPeriode(courante, bareme.aujourdhui).classe}`}>
              {etatDeLaPeriode(courante, bareme.aujourdhui).mot}
            </span>
          </>
        )}
        {" · "}
        <button
          type="button"
          className="lien"
          onClick={() => {
            setOuvert(!ouvert);
          }}
        >
          {ouvert ? "fermer" : "gérer les périodes"}
        </button>
      </p>

      {ouvert && (
        <div className="periodes__panneau">
          {courante !== null && (
            <form
              className="ajout"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <span className="periodes__titre">Corriger « {courante.libelle} »</span>
              <Champ
                valeur={courante.libelle}
                enAttente={enAttente}
                ariaLabel="Libellé de la période de paniers"
                onValider={(valeur) => {
                  onAgir(modifierPeriodePanier(courante.id, { libelle: valeur }));
                }}
              />
              <BorneDate
                valeur={courante.debut}
                etiquette="Début"
                enAttente={enAttente}
                onValider={(valeur) => {
                  onAgir(modifierPeriodePanier(courante.id, { debut: valeur }));
                }}
              />
              <BorneDate
                valeur={courante.fin}
                etiquette="Fin"
                enAttente={enAttente}
                onValider={(valeur) => {
                  onAgir(modifierPeriodePanier(courante.id, { fin: valeur }));
                }}
              />
              {/* Le serveur REFUSE d'effacer une période qui porte des zones — c'est
                  l'historique. Le seul cas qui passe est celle qu'on vient de créer de travers. */}
              <button
                type="button"
                className="lien"
                disabled={enAttente}
                onClick={() => {
                  onAgir(supprimerPeriodePanier(courante.id));
                }}
              >
                Supprimer
              </button>
            </form>
          )}

          <form
            className="ajout"
            onSubmit={(e) => {
              e.preventDefault();
              if (!complet) return;
              const demande = { libelle: libelle.trim(), debut, fin };
              onAgir(
                depuis === "" ? creerPeriodePanier(demande) : dupliquerPeriodePanier(depuis, demande),
              );
            }}
          >
            <span className="periodes__titre">Nouvelle période</span>
            <input
              value={libelle}
              placeholder="Libellé"
              aria-label="Libellé de la nouvelle période de paniers"
              onChange={(e) => {
                setLibelle(e.target.value);
              }}
            />
            <label>
              du{" "}
              <input
                type="date"
                value={debut}
                aria-label="Début de la nouvelle période de paniers"
                onChange={(e) => {
                  setDebut(e.target.value);
                }}
              />
            </label>
            <label>
              au{" "}
              <input
                type="date"
                value={fin}
                aria-label="Fin de la nouvelle période de paniers"
                onChange={(e) => {
                  setFin(e.target.value);
                }}
              />
            </label>
            {bareme.periodes.length > 0 && (
              <label>
                en reprenant les zones de{" "}
                <select
                  value={depuis}
                  aria-label="Période de paniers dont reprendre les zones"
                  onChange={(e) => {
                    setDepuis(e.target.value);
                  }}
                >
                  <option value="">aucune (barème vide)</option>
                  {bareme.periodes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.libelle}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button type="submit" disabled={!complet || enAttente}>
              Créer
            </button>
          </form>

          <p className="note">
            Deux périodes ne peuvent pas se chevaucher : le panier d'un jour donné doit être
            décidable. Reprendre les zones d'une période donne un point de départ à corriger, pas
            une reconduction. La période passée reste : c'est elle qui porte l'historique. Les
            périodes des paniers sont <strong>indépendantes</strong> de celles des indemnités.
          </p>
        </div>
      )}
    </div>
  );
}

type EtatPaniers =
  | { phase: "chargement" }
  | { phase: "charge"; bareme: BaremePaniers }
  | { phase: "erreur"; raison: string };

/**
 * Le tableau des paniers par zone.
 *
 * *Demande de Chris, 02/09/2026, classeur `Paniers.xlsx` à l'appui, et placé ici sur sa consigne :
 * « juste sous la table transport ».*
 *
 * ## Les bornes ne se lisent PAS comme celles des indemnités
 *
 * Le classeur des indemnités porte le chevron sur la borne **basse** (`>10`), celui des paniers
 * sur la borne **haute** (`<75`) : Zone A va de 50 à *moins de* 75, et Zone B commence à 75. Une
 * distance de 75 km est donc un panier de Zone B, alors qu'une distance de 10 km est une
 * indemnité de Zone1.
 *
 * Les deux conventions sont l'inverse l'une de l'autre, chacune est cohérente avec elle-même, et
 * **on n'en harmonise aucune** : réécrire l'une pour ressembler à l'autre déplacerait de l'argent
 * sur les trajets qui tombent exactement sur une borne. L'écran nomme donc la règle dans ses
 * en-têtes — « à partir de » / « en dessous de » — et réaffiche le chevron du côté où le classeur
 * l'écrit, pour que Chris reconnaisse son tableau.
 *
 * ## Une seule colonne de montant
 *
 * Contrairement aux indemnités, ce classeur ne porte pas de règle de calcul déguisée : que des
 * euros. Une case vide reste « pas renseigné », jamais zéro, et se compte.
 */
function PaniersParZone(): JSX.Element {
  const [etat, setEtat] = useState<EtatPaniers>({ phase: "chargement" });
  const [voulue, setVoulue] = useState<string | null>(null);
  const [enAttente, setEnAttente] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [bilan, setBilan] = useState<BilanPaniers | null>(null);
  const [nouvelle, setNouvelle] = useState({ libelle: "", montant: "", kmMin: "", kmMax: "" });
  const champFichier = useRef<HTMLInputElement>(null);

  const recharger = (id: string | null = voulue): void => {
    chargerBaremePaniers(id)
      .then((bareme) => {
        setEtat({ phase: "charge", bareme });
      })
      .catch((e: unknown) => {
        setEtat({ phase: "erreur", raison: e instanceof Error ? e.message : String(e) });
      });
  };

  useEffect(() => {
    recharger(voulue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voulue]);

  const agir = (action: Promise<unknown>): void => {
    setEnAttente(true);
    setErreur(null);
    action
      .then(() => {
        recharger();
      })
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setEnAttente(false);
      });
  };

  if (etat.phase === "chargement") return <p>Chargement des paniers…</p>;
  if (etat.phase === "erreur") return <p className="alerte">{etat.raison}</p>;

  const { bareme } = etat;
  const periode = bareme.periode;
  const { zones, coherence } = bareme;
  const complet = nouvelle.libelle.trim() !== "" && nouvelle.kmMin !== "" && nouvelle.kmMax !== "";

  return (
    <section className="referentiel">
      <h2>
        Paniers par zone — {zones.length} zone(s)
        {periode !== null && <> · {periode.libelle}</>}
      </h2>
      <p className="etat">
        Une zone de panier est une <strong>tranche de kilomètres</strong> et le montant du panier
        correspondant. Les bornes se lisent «{" "}
        <strong>à partir de la basse, en dessous de la haute</strong> » — c'est le « &lt; » du
        classeur : une distance de 75 km est dans la zone qui commence à 75, pas dans celle qui
        s'arrête avant. <strong>C'est l'inverse des zones d'indemnité</strong>, où la borne
        appartient à la tranche du dessous ; chaque tableau garde la règle de son classeur.
      </p>

      <PeriodesPanier bareme={bareme} enAttente={enAttente} onChoisir={setVoulue} onAgir={agir} />

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      {periode === null ? (
        <p className="alerte">
          Aucune période de validité : le barème des paniers n'a pas encore d'année. En créer une
          ci-dessus — la proposition porte déjà les bonnes bornes.
        </p>
      ) : (
        <>
          {/* Ce qui manque ou ce qui se contredit se COMPTE et s'affiche. */}
          <p className="etat">
            {coherence.sansMontant.length === 0 ? (
              <>Toutes les zones portent un montant.</>
            ) : (
              <strong>
                {coherence.sansMontant.length} zone(s) sans montant — {coherence.sansMontant.join(", ")}.
              </strong>
            )}
            {" · "}
            {coherence.chevauchements.length === 0 && coherence.trous.length === 0 ? (
              <>
                Les tranches s'enchaînent sans trou
                {coherence.couverteDepuis !== null && coherence.couverteJusqua !== null && (
                  <>
                    , de {borneLisible(coherence.couverteDepuis)} à moins de{" "}
                    {borneLisible(coherence.couverteJusqua)} km
                  </>
                )}
                .
              </>
            ) : (
              <strong>
                {coherence.chevauchements.length > 0 && (
                  <>
                    {coherence.chevauchements.map((c) => `${c.premiere} recouvre ${c.seconde}`).join(" ; ")}.{" "}
                  </>
                )}
                {coherence.trous.length > 0 && (
                  <>
                    Aucune zone entre {coherence.trous.map((t) => `${t.apres} et ${t.avant}`).join(" ; ")}.
                  </>
                )}
              </strong>
            )}
            {/*
              La borne basse du barème n'est pas un trou entre deux zones, mais c'est quand même
              une distance sans panier — et le classeur commence à 50, pas à 0. Sans cette phrase,
              un trajet de 30 km n'aurait aucun panier et l'écran resterait muet là-dessus.
            */}
            {coherence.couverteDepuis !== null && Number(coherence.couverteDepuis) > 0 && (
              <>
                {" "}
                En dessous de {borneLisible(coherence.couverteDepuis)} km, aucune zone : pas de
                panier.
              </>
            )}
          </p>

          <p className="etat">
            <a href={adresseClasseurPaniers(periode.id)} download>
              Exporter le classeur
            </a>
            {" · "}
            <button type="button" className="lien" onClick={() => champFichier.current?.click()}>
              Réimporter un classeur dans « {periode.libelle} »
            </button>
            <input
              ref={champFichier}
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={(e) => {
                const fichier = e.target.files?.[0];
                e.target.value = "";
                if (fichier === undefined) return;
                setBilan(null);
                setErreur(null);
                importerBaremePaniers(periode.id, fichier)
                  .then((resultat) => {
                    setBilan(resultat);
                    recharger();
                  })
                  .catch((err: unknown) => {
                    setErreur(err instanceof Error ? err.message : String(err));
                  });
              }}
            />
            {" · "}
            <span className="note">
              Les zones de cette période sont <strong>remplacées</strong> par celles du classeur —
              une zone absente disparaît. Déposer ici le classeur des zones d'indemnité est refusé :
              les deux ont les mêmes colonnes de kilomètres, et l'un remplacerait l'autre en
              silence.
            </span>
          </p>

          {bilan !== null && <BilanPaniersLu bilan={bilan} />}

          <div className="transport__defilement">
            <table className="ressources transport__table paniers__table">
              {/* La grille des colonnes : sans elle, `table-layout: fixed` sur `width: 100%`
                  partage la fenêtre à parts égales. */}
              <colgroup>
                <col style={{ width: "calc(16ch + 0.7rem)" }} />
                <col style={{ width: "calc(14ch + 0.7rem)" }} />
                <col style={{ width: "calc(14ch + 0.7rem)" }} />
                <col style={{ width: "calc(16ch + 0.7rem)" }} />
                <col style={{ width: "5.5rem" }} />
              </colgroup>
              <thead>
                {/* L'ordre des colonnes est celui du classeur de Chris : le montant avant les
                    bornes. Le tableau doit se relire face à son fichier. */}
                <tr>
                  <th>Zone panier</th>
                  <th className="col-nombre">Panier (€)</th>
                  <th className="col-nombre">Km à partir de</th>
                  <th className="col-nombre">Km en dessous de</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {zones.map((z) => (
                  <tr key={z.id}>
                    <td>
                      <Champ
                        valeur={z.libelle}
                        enAttente={enAttente}
                        ariaLabel={`Nom de la zone de panier ${z.libelle}`}
                        onValider={(valeur) => {
                          if (valeur !== "") agir(modifierZonePanier(z.id, { libelle: valeur }));
                        }}
                      />
                    </td>
                    <td className="col-nombre">
                      <Champ
                        valeur={z.montant ?? ""}
                        placeholder="—"
                        enAttente={enAttente}
                        ariaLabel={`Montant du panier de ${z.libelle}`}
                        onValider={(valeur) => {
                          agir(modifierZonePanier(z.id, { montant: valeur === "" ? null : valeur }));
                        }}
                      />
                    </td>
                    <td className="col-nombre">
                      <Champ
                        valeur={borneLisible(z.kmMin)}
                        enAttente={enAttente}
                        ariaLabel={`Borne basse de ${z.libelle}`}
                        onValider={(valeur) => {
                          if (valeur !== "") agir(modifierZonePanier(z.id, { kmMin: valeur }));
                        }}
                      />
                    </td>
                    <td className="col-nombre">
                      {/*
                        Le chevron est sur la borne HAUTE ici, et il vaut « < » : c'est ce que le
                        classeur des paniers écrit, et l'inverse de celui des indemnités. Il est
                        réaffiché, jamais stocké — la donnée est le nombre.

                        Dans le conteneur en `flex`, pour la même raison que la borne des
                        indemnités : `table.ressources .saisie` impose `width: 100%`, donc un
                        chevron simplement posé devant pousserait le champ hors de sa cellule.
                      */}
                      <span className="zones__borne">
                        <span className="zones__chevron">&lt;</span>
                        <Champ
                          valeur={borneLisible(z.kmMax)}
                          enAttente={enAttente}
                          ariaLabel={`Borne haute de ${z.libelle}`}
                          onValider={(valeur) => {
                            if (valeur !== "") agir(modifierZonePanier(z.id, { kmMax: valeur }));
                          }}
                        />
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="lien"
                        disabled={enAttente}
                        title="Retirer cette zone du barème de cette période. Les autres périodes n'y touchent pas."
                        onClick={() => {
                          agir(supprimerZonePanier(z.id));
                        }}
                      >
                        Retirer
                      </button>
                    </td>
                  </tr>
                ))}
                {zones.length === 0 && (
                  <tr>
                    <td colSpan={5} className="vide">
                      Aucune zone de panier dans cette période. En ajouter une ci-dessous,
                      réimporter le classeur, ou créer la période en reprenant les zones d'une
                      autre.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <form
            className="ajout"
            onSubmit={(e) => {
              e.preventDefault();
              if (!complet) return;
              const demande = {
                libelle: nouvelle.libelle.trim(),
                kmMin: nouvelle.kmMin,
                kmMax: nouvelle.kmMax,
                montant: nouvelle.montant === "" ? null : nouvelle.montant,
              };
              setNouvelle({ libelle: "", montant: "", kmMin: "", kmMax: "" });
              agir(ajouterZonePanier(periode.id, demande));
            }}
          >
            <span className="periodes__titre">Nouvelle zone de panier</span>
            <input
              value={nouvelle.libelle}
              placeholder="Zone C"
              aria-label="Nom de la nouvelle zone de panier"
              onChange={(e) => {
                setNouvelle({ ...nouvelle, libelle: e.target.value });
              }}
            />
            <label>
              panier{" "}
              <input
                className="ajout__nombre"
                value={nouvelle.montant}
                placeholder="€"
                aria-label="Montant du panier de la nouvelle zone"
                onChange={(e) => {
                  setNouvelle({ ...nouvelle, montant: e.target.value });
                }}
              />
            </label>
            <label>
              à partir de{" "}
              <input
                className="ajout__nombre"
                value={nouvelle.kmMin}
                placeholder="km"
                aria-label="Borne basse de la nouvelle zone de panier"
                onChange={(e) => {
                  setNouvelle({ ...nouvelle, kmMin: e.target.value });
                }}
              />
            </label>
            <label>
              en dessous de{" "}
              <input
                className="ajout__nombre"
                value={nouvelle.kmMax}
                placeholder="km"
                aria-label="Borne haute de la nouvelle zone de panier"
                onChange={(e) => {
                  setNouvelle({ ...nouvelle, kmMax: e.target.value });
                }}
              />
            </label>
            <button type="submit" disabled={!complet || enAttente}>
              Ajouter la zone
            </button>
          </form>
          <p className="note">
            Un montant laissé vide est permis — c'est un panier qui reste à trancher, et l'écran le
            compte. Zéro euro, lui, veut dire zéro.
          </p>
        </>
      )}
    </section>
  );
}

/**
 * Une des deux tables.
 *
 * Elle se charge et se recharge seule : une panne sur l'une ne doit pas emporter l'autre, comme
 * les carrières et la grille tarifaire sur l'écran des référentiels.
 */
function Table({
  table,
  materiels,
}: {
  table: TableTransport;
  materiels: readonly Generique[];
}): JSX.Element {
  const forme = TABLES[table];
  const [etat, setEtat] = useState<Etat>({ phase: "chargement" });
  const [enAttente, setEnAttente] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [bilan, setBilan] = useState<BilanTransport | null>(null);
  const [ajout, setAjout] = useState("");
  const champFichier = useRef<HTMLInputElement>(null);

  const recharger = (): void => {
    chargerTransport(table)
      .then((contenu) => {
        setEtat({ phase: "chargee", contenu });
      })
      .catch((e: unknown) => {
        setEtat({ phase: "erreur", raison: e instanceof Error ? e.message : String(e) });
      });
  };

  useEffect(recharger, [table]);

  /** Tout geste passe par ici : une seule façon d'attendre, d'échouer et de recharger. */
  const agir = (cle: string | null, action: Promise<unknown>): void => {
    setEnAttente(cle);
    setErreur(null);
    action
      .then(recharger)
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setEnAttente(null);
      });
  };

  if (etat.phase === "chargement") return <p>Chargement…</p>;
  if (etat.phase === "erreur") return <p className="alerte">{etat.raison}</p>;

  const { lignes, groupes } = etat.contenu;
  const dejaLa = new Set(lignes.map((l) => l.ressourceId));
  const disponibles = materiels.filter((m) => m.actif && !dejaLa.has(m.id));
  const sansTonnage = forme.avecTonnage
    ? lignes.filter((l) => (l.tonnage ?? null) === null).length
    : 0;
  /* Une ligne qui n'est dans aucun groupe : c'est ce que le responsable transport doit voir en
     premier — elle comptera pour un voyage à elle seule, et personne ne l'a décidé. */
  const dansUnGroupe = new Set(groupes.flatMap((g) => g.membres));
  const isolees = lignes.filter((l) => !dansUnGroupe.has(l.id)).length;

  return (
    <section className="referentiel">
      <h2>
        {forme.titre} — {lignes.length} ligne(s), {groupes.length} {forme.mots}
      </h2>
      <p className="etat">{forme.aide}</p>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      {/* Ce qui manque se COMPTE et s'affiche. Un tableau qui se tait se lit « tout va bien ». */}
      <p className="etat">
        {forme.avecTonnage && (
          <>
            {sansTonnage === 0 ? (
              <>Tous les tonnages sont renseignés.</>
            ) : (
              <strong>{sansTonnage} ligne(s) sans tonnage — à compléter.</strong>
            )}
            {" · "}
          </>
        )}
        {isolees === 0 ? (
          <>Chaque ligne appartient à au moins un {forme.mot}.</>
        ) : (
          <strong>
            {isolees} ligne(s) dans aucun {forme.mot}
            {forme.avecTonnage ? "" : " — comptée(s) pour un voyage chacune"}.
          </strong>
        )}
      </p>

      {/*
        **D'où vient cette liste, dit à l'écran** (Chris, 01/09/2026 : « la table de référence
        pour l'ajout du matériel doit être la table matériel dans les ressources génériques »).

        Elle en venait déjà — le serveur refuse même un code de main-d'œuvre à l'écriture. Ce
        qui manquait, c'est que l'écran le DISE : une fois le classeur importé, les 62 matériels
        du référentiel sont tous dans la table, la liste déroulante n'a plus rien à proposer, et
        un menu vide ne dit pas d'où il tire ses choix. On lisait « la liste ne marche pas » là
        où il fallait lire « il n'y a plus rien à ajouter ».

        C'est la règle de la donnée manquante appliquée à un menu : ce qui est vide doit dire
        pourquoi il l'est.
      */}
      <p className="etat">
        Le choix vient de la <strong>table Matériel des ressources génériques</strong> :{" "}
        {materiels.length - disponibles.length} de ses {materiels.length} codes sont déjà dans
        cette table.{" "}
        {disponibles.length === 0 ? (
          <strong>
            Il n'en reste aucun à ajouter — pour paramétrer une ligne de plus, créer d'abord la
            ressource générique dans « Ressources génériques ».
          </strong>
        ) : (
          <>
            {disponibles.length} encore disponible(s). Un matériel qui n'y figure pas doit y être
            créé d'abord : c'est son code qui rattachera la ligne au chiffrage.
          </>
        )}
      </p>

      <div className="ajout">
        <select
          className="ajout__libelle"
          value={ajout}
          disabled={disponibles.length === 0}
          aria-label={`Matériel à ajouter aux ${forme.mots}`}
          onChange={(e) => {
            setAjout(e.target.value);
          }}
        >
          <option value="">
            {disponibles.length === 0
              ? "Tout le référentiel matériel est déjà là"
              : "Ajouter un matériel…"}
          </option>
          {disponibles.map((m) => (
            <option key={m.id} value={m.id}>
              {m.code} — {m.libelle}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={ajout === "" || enAttente !== null}
          onClick={() => {
            const choisi = ajout;
            setAjout("");
            agir(choisi, ajouterLigneTransport(table, choisi, null));
          }}
        >
          Ajouter la ligne
        </button>
        <button
          type="button"
          disabled={enAttente !== null}
          onClick={() => {
            agir("groupe", creerGroupeTransport(table));
          }}
        >
          Ajouter un {forme.mot}
        </button>
      </div>

      {/*
        L'aller-retour par Excel, comme les carrières et la grille tarifaire. La différence est
        dite AVANT le clic, parce qu'elle surprend : les lignes s'ajoutent, les colonnes se
        remplacent. Un classeur d'hier réimporté aujourd'hui rendrait le paramétrage d'hier.
      */}
      <p className="etat">
        <a href={adresseClasseurTransport(table)} download>
          Exporter le classeur
        </a>
        {" · "}
        <button
          type="button"
          className="lien"
          onClick={() => champFichier.current?.click()}
        >
          Réimporter un classeur
        </button>
        <input
          ref={champFichier}
          type="file"
          accept=".xlsx"
          hidden
          onChange={(e) => {
            const fichier = e.target.files?.[0];
            e.target.value = "";
            if (fichier === undefined) return;
            setBilan(null);
            setErreur(null);
            importerTransport(table, fichier)
              .then((resultat) => {
                setBilan(resultat);
                recharger();
              })
              .catch((err: unknown) => {
                setErreur(err instanceof Error ? err.message : String(err));
              });
          }}
        />
        {" · "}
        <span className="note">
          Une ligne absente du classeur n'est jamais supprimée ; les {forme.mots}, eux, sont
          remplacés par ceux du classeur.
        </span>
      </p>

      {bilan !== null && <Bilan bilan={bilan} />}

      {groupes.length === 0 && lignes.length > 0 && (
        <p className="alerte">
          Aucun {forme.mot} : le tableau n'a pas de colonne. En ajouter un ci-dessus, ou
          réimporter le classeur.
        </p>
      )}

      <div className="transport__defilement">
        <table className="ressources transport__table">
          {/*
            La grille des colonnes. Sans elle, `table-layout: fixed` sur `width: 100%` partage
            la fenêtre à parts égales et le libellé d'une machine reçoit la largeur d'une case
            à cocher — la panne corrigée le 01/09 sur les trois autres référentiels.

            Les largeurs de tête sont celles mesurées sur les 62 matériels du classeur : code 12
            caractères au plus long, libellé 34, famille 28.
          */}
          <colgroup>
            <col style={{ width: "calc(14ch + 0.7rem)" }} />
            <col style={{ width: "calc(36ch + 0.7rem)" }} />
            <col style={{ width: "calc(30ch + 0.7rem)" }} />
            {forme.avecTonnage && <col style={{ width: "calc(10ch + 0.7rem)" }} />}
            {groupes.map((g) => (
              <col key={g.id} style={{ width: "4.6rem" }} />
            ))}
            <col style={{ width: "5.5rem" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Code</th>
              <th>Libellé</th>
              <th>Famille</th>
              {forme.avecTonnage && <th className="col-nombre">Tonnage</th>}
              {groupes.map((g) => (
                <th key={g.id} className="transport__colonne">
                  <span>
                    {forme.mot === "couple" ? "Couple" : "Combi"} {g.numero}
                  </span>
                  <button
                    type="button"
                    className="lien"
                    title={`Supprimer le ${forme.mot} ${String(g.numero)}. Les lignes qu'il désigne restent dans la table.`}
                    disabled={enAttente !== null}
                    onClick={() => {
                      agir(g.id, supprimerGroupeTransport(table, g.id));
                    }}
                  >
                    ✕
                  </button>
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {lignes.map((l) => (
              <tr key={l.id} className={l.actif ? undefined : "retiree"}>
                <td className="code" title={l.code}>
                  {l.code}
                </td>
                <td className="transport__libelle" title={l.libelle}>
                  {l.libelle}
                </td>
                <td className="famille" title={l.familleLibelle ?? undefined}>
                  {l.familleLibelle ?? ""}
                </td>
                {forme.avecTonnage && (
                  <td className="col-nombre">
                    <Champ
                      valeur={l.tonnage ?? ""}
                      placeholder="—"
                      enAttente={enAttente === l.id}
                      ariaLabel={`Tonnage de ${l.code}`}
                      onValider={(valeur) => {
                        agir(l.id, fixerTonnageTransport(l.id, valeur === "" ? null : valeur));
                      }}
                    />
                  </td>
                )}
                {groupes.map((g) => {
                  const coche = g.membres.includes(l.id);
                  const cle = `${g.id}|${l.id}`;
                  return (
                    <td key={g.id} className="col-duree">
                      <input
                        type="checkbox"
                        checked={coche}
                        disabled={enAttente !== null}
                        aria-label={`${l.code} dans le ${forme.mot} ${String(g.numero)}`}
                        onChange={() => {
                          agir(cle, cocherTransport(table, g.id, l.id, !coche));
                        }}
                      />
                    </td>
                  );
                })}
                <td>
                  <button
                    type="button"
                    className="lien"
                    disabled={enAttente !== null}
                    title="Retirer ce matériel de CETTE table. Il reste au référentiel matériel."
                    onClick={() => {
                      agir(l.id, retirerLigneTransport(table, l.id));
                    }}
                  >
                    Retirer
                  </button>
                </td>
              </tr>
            ))}
            {lignes.length === 0 && (
              <tr>
                <td colSpan={groupes.length + (forme.avecTonnage ? 5 : 4)} className="vide">
                  Aucun matériel dans cette table. En ajouter un, ou réimporter le classeur.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function ParametrageTransport(): JSX.Element {
  const [materiels, setMateriels] = useState<readonly Generique[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    chargerGeneriques()
      .then(({ generiques }) => {
        setMateriels(generiques.filter((g) => g.type === "MAT"));
      })
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      });
  }, []);

  return (
    <>
      <p className="etat">
        Ce que le responsable transport lira pour organiser ses camions : le barème des{" "}
        <strong>indemnités par zone</strong>, les deux tables de matériel, et le barème des{" "}
        <strong>paniers par zone</strong>. Les quatre sont <strong>indépendantes</strong> :
        ajouter un matériel, un groupe ou une zone dans l'une ne touche pas les autres, et les
        deux barèmes ont chacun leurs propres périodes de validité. Les colonnes descriptives des
        deux tables de matériel viennent du référentiel matériel et s'y corrigent — ici se
        saisissent le tonnage et les croix.
      </p>
      {/*
        Les deux barèmes de kilomètres ne lisent PAS leurs bornes dans le même sens, et c'est dit
        en tête d'écran plutôt que dans chaque tableau : ils sont à trois centimètres l'un de
        l'autre, et un lecteur qui suppose la même règle des deux côtés se trompe d'une zone sur
        les trajets qui tombent exactement sur une borne.
      */}
      <p className="etat">
        <strong>Attention aux bornes de kilomètres</strong> : les deux barèmes ne les lisent pas
        dans le même sens, chacun gardant la règle de son classeur. Une indemnité se lit «{" "}
        <strong>au-delà de</strong> la borne basse, jusqu'à la haute incluse » — 10 km est en
        Zone1. Un panier se lit « <strong>à partir de</strong> la borne basse, en dessous de la
        haute » — 75 km est en Zone B, pas en Zone A.
      </p>
      {/*
        Dit en tête, et pas seulement au-dessus de chaque liste : c'est l'ordre des gestes qui
        surprend, et on ne le découvre qu'au moment où le menu déroulant est vide.
      */}
      <p className="etat">
        <strong>L'ordre des gestes</strong> : les deux tables puisent dans la table{" "}
        <strong>Matériel</strong> des ressources génériques. Un engin qui n'y est pas ne peut pas
        être paramétré ici — il faut d'abord le créer dans « Ressources génériques », puis
        revenir l'ajouter à l'une ou l'autre table. C'est son code qui le rattache au chiffrage,
        et un code inventé ici ne se retrouverait dans aucun déboursé.
      </p>

      {erreur !== null && <p className="alerte">{erreur}</p>}
      {materiels === null && erreur === null && <p>Chargement du référentiel matériel…</p>}

      {/*
        Les indemnités par zone viennent EN PREMIER, sur la consigne de Chris du 02/09/2026 :
        « avant le tableau transport de matériaux ». Elle se charge seule et ne dépend pas du
        référentiel matériel — une panne sur l'un ne doit pas emporter l'autre, et c'est aussi
        pourquoi elle est hors du bloc conditionné par `materiels`.
      */}
      /* Les réglages du CALCUL viennent en tête : ce sont eux qui donnent un sens aux
         tonnages saisis juste en dessous, et ils tiennent en trois lignes. */
      <ReglagesTransport />

      <ZonesIndemnite />

      {/*
        Les paniers viennent JUSTE SOUS la table du transport de matériaux, sur la consigne de
        Chris du 02/09/2026. Ils sont donc rendus entre les deux tables de matériel, et non dans
        le même bloc : le barème des paniers ne dépend d'aucun code matériel, et le mettre sous la
        condition `materiels !== null` l'aurait fait disparaître le jour où l'appel au référentiel
        échoue — alors qu'il n'a rien à voir avec lui.
      */}
      {materiels !== null && <Table table="couples" materiels={materiels} />}

      <PaniersParZone />

      {materiels !== null && <Table table="combinaisons" materiels={materiels} />}
    </>
  );
}
